import type { PaginationInfo, SortConfig, FilterValues } from '@/shared/components';
import type { TenantFormData, TenantFormInput } from '../schema/tenant.schema';

/** Stripe / portal-admin KYC values (aligned with API). */
export type TenantKycStatus = 'pending' | 'completed' | 'rejected';

/** API `details` object for create / patch (portal-admin tenants). */
export interface TenantDetailsDto {
  displayName: string;
  logo: string | null;
  supportEmail: string | null;
  vollmachtUrl: string | null;
  primaryEmail: string | null;
  city: string | null;
  zipCode: string | null;
  streetAddress: string | null;
}

/** POST /portal-admin/tenants */
export interface CreateTenantRequest {
  tenantName: string;
  tenantCode: string;
  /** ISO 3166-1 alpha-2 (e.g. DE). */
  country: string;
  /** Bexio email template identifier used by the backend. */
  bexioTemplateSlug: string;
  /** Per-customer commission for tenants with ≤50 registered users. */
  commissionUnder50: number;
  /** Per-customer commission for tenants with 50–150 registered users. */
  commission50To150: number;
  /** Per-customer commission for tenants with more than 150 registered users. */
  commissionOver150: number;
  details: TenantDetailsDto;
}

/** PATCH /portal-admin/tenants/{tenantId} — only include fields you want to change. */
export interface PatchTenantRequest {
  tenantName?: string;
  tenantCode?: string;
  /** ISO 3166-1 alpha-2 when updating Stripe / portal country. */
  country?: string;
  /** Bexio email template identifier used by the backend. */
  bexioTemplateSlug?: string;
  isActive?: boolean;
  commissionUnder50?: number;
  commission50To150?: number;
  commissionOver150?: number;
  details?: Partial<TenantDetailsDto>;
}

export interface Tenant {
  TenantId: string;
  TenantName: string;
  /** Short unique code for the tenant (API). */
  TenantCode?: string;
  Email: string;
  City: string;
  ZipCode: string;
  StreetAddress: string;
  IBAN: string;
  SupportEmail: string;
  PlanAmount?: number;
  KycStatus: TenantKycStatus;
  /** Short label for revenue / fee configuration */
  RevenueConfiguration: string;
  CommissionUnder50?: number;
  Commission50To150?: number;
  CommissionOver150?: number;
  /** Absolute or same-origin URL to the tenant logo image (when returned by the API). */
  LogoUrl?: string;
  LogoFileName?: string;
  /** Bumps logo image cache after client-side upload (not from API). */
  LogoCacheBust?: number;
  /** Power of attorney document URL when API returns `vollmachtUrl` in details. */
  PoaUrl?: string;
  PoaFileName?: string;
  /** ISO 3166-1 alpha-2 when API returns country / StripeConnectDefaultCountry. */
  Country?: string;
  /** Bexio email template identifier when returned by GET tenant by id. */
  BexioTemplateSlug?: string;
}

export type TenantRecord = Tenant & Record<string, unknown>;

export interface TenantsListResponse {
  message: string;
  data: {
    count: number;
    tenants: Tenant[];
  };
}

/** GET /portal-admin/tenants/{tenantId}/delete-eligibility */
export interface TenantDeleteEligibility {
  canDelete: boolean;
  customerCount: number;
  message: string;
}

/**
 * GET /portal-admin/tenants filters (mapped in the service).
 * Set `includeInactive: true` only for tenant management (active + inactive);
 * omit or leave false for dropdowns (active tenants only — no `includeInactive` query param).
 */
export interface TenantFilters {
  search?: string;
  /** UI filter; sent as `stripeKycStatus` on the wire. */
  kycStatus?: TenantKycStatus | 'all';
  page?: number;
  limit?: number;
  /** When true, request includes inactive tenants (`includeInactive=true`). */
  includeInactive?: boolean;
}

/** Maps table KYC filter to portal-admin `stripeKycStatus` query (same enum as API). */
export function tenantKycToStripeQueryParam(
  kyc: TenantKycStatus | 'all' | undefined
): string | undefined {
  if (!kyc || kyc === 'all') return undefined;
  return kyc;
}

/** Normalizes filters from URL or legacy values onto `pending` | `completed` | `rejected`. */
export function coerceTenantKycFilter(
  raw: string | undefined | null
): TenantKycStatus | 'all' {
  if (!raw || raw === 'all') return 'all';
  if (raw === 'pending' || raw === 'completed' || raw === 'rejected') {
    return raw;
  }
  if (raw === 'in_review' || raw === 'in-review') return 'pending';
  if (raw === 'approved') return 'completed';
  return 'all';
}

export function buildCreateTenantPayload(
  data: TenantFormData
): CreateTenantRequest {
  const country = (data.country ?? '').trim().toUpperCase();
  return {
    tenantName: data.tenantName.trim(),
    tenantCode: data.tenantCode.trim(),
    country,
    bexioTemplateSlug: data.bexioTemplateSlug.trim(),
    commissionUnder50: data.commissionUnder50,
    commission50To150: data.commission50To150,
    commissionOver150: data.commissionOver150,
    details: {
      displayName: data.tenantName.trim(),
      logo: data.logoBase64 || null,
      supportEmail: data.supportEmail.trim() || null,
      vollmachtUrl: data.poaBase64 || null,
      primaryEmail: data.email.trim() || null,
      city: data.city.trim() || null,
      zipCode: data.zipCode.trim() || null,
      streetAddress: data.streetAddress.trim() || null,
    },
  };
}

export function buildPatchTenantPayload(
  data: TenantFormData
): PatchTenantRequest {
  const details: Partial<TenantDetailsDto> = {
    displayName: data.tenantName.trim(),
    supportEmail: data.supportEmail.trim() || null,
    city: data.city.trim() || null,
    zipCode: data.zipCode.trim() || null,
    streetAddress: data.streetAddress.trim() || null,
  };
  if (data.logoBase64) {
    details.logo = data.logoBase64;
  }
  if (data.poaBase64) {
    details.vollmachtUrl = data.poaBase64;
  }
  const payload: PatchTenantRequest = {
    tenantName: data.tenantName.trim(),
    tenantCode: data.tenantCode.trim(),
    bexioTemplateSlug: data.bexioTemplateSlug.trim(),
    commissionUnder50: data.commissionUnder50,
    commission50To150: data.commission50To150,
    commissionOver150: data.commissionOver150,
    details,
  };
  return payload;
}

export function tenantToFormDefaults(tenant: Tenant): TenantFormInput {
  return {
    bexioTemplateSlug: tenant.BexioTemplateSlug ?? '',
    tenantName: tenant.TenantName,
    tenantCode: tenant.TenantCode ?? '',
    email: tenant.Email,
    city: tenant.City,
    zipCode: tenant.ZipCode,
    streetAddress: tenant.StreetAddress,
    iban: '',
    country: tenant.Country ?? '',
    supportEmail: tenant.SupportEmail,
    logoBase64: '',
    logoFileName: tenant.LogoFileName ?? '',
    poaBase64: '',
    poaFileName: tenant.PoaFileName ?? '',
    commissionUnder50: tenant.CommissionUnder50 ?? '',
    commission50To150: tenant.Commission50To150 ?? '',
    commissionOver150: tenant.CommissionOver150 ?? '',
  };
}

export interface TenantDataTableProps {
  tenants: Tenant[];
  loading?: boolean;
  pagination: PaginationInfo;
  currentFilters: FilterValues;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (sort: SortConfig | null) => void;
  onFilterChange: (filters: FilterValues) => void;
  onRowClick?: (tenant: Tenant) => void;
  onDelete?: (tenant: Tenant) => void;
  className?: string;
}

export interface TenantFormProps {
  onSubmit: (data: TenantFormData) => void;
  mode?: 'create' | 'edit';
  /** Merged with default empty values; used for edit prefills. */
  defaultValues?: Partial<TenantFormInput>;
  isLoading?: boolean;
  error?: string;
  className?: string;
  translate: (key: string) => string;
  onDirtyChange?: (dirty: boolean) => void;
}
